import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Drawer, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { fetchAllocationHistory } from "../../../../../../api/associates";

// ── Helpers ───────────────────────────────────────────────────────────────────

const AVATAR_COLORS = ["#28B3B4", "#D6DF27", "#4a9960", "#7a5abd", "#c88a3a"];
const avatarColor = (name = "") =>
  AVATAR_COLORS[((name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0)) % AVATAR_COLORS.length];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const groupByYear = (items) => {
  const groups = {};
  items.forEach((item) => {
    const year = new Date(item.startDate).getFullYear();
    if (!groups[year]) groups[year] = [];
    groups[year].push(item);
  });
  return Object.entries(groups).sort(([a], [b]) => Number(b) - Number(a));
};

// ── Sub-components ────────────────────────────────────────────────────────────

const TypePill = ({ type, t }) => {
  const isBil = type === "BIL";
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 7px", borderRadius: "2px",
      background: isBil ? t.accent2 : "rgba(214,223,39,0.12)",
      border: `1px solid ${isBil ? `${t.accent}44` : "rgba(214,223,39,0.28)"}`,
    }}>
      <Typography sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "8.5px", fontWeight: 700,
        letterSpacing: "0.06em",
        color: isBil ? t.accent : t.gold,
      }}>
        {type}
      </Typography>
    </Box>
  );
};

const StatusChip = ({ status, t }) => {
  const isActive = status === "ACTIVE";
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 6px", borderRadius: "2px",
      background: isActive ? "rgba(40,179,180,0.10)" : t.surface3,
      border: `1px solid ${isActive ? "rgba(40,179,180,0.22)" : t.border2}`,
    }}>
      <Typography sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "8px", fontWeight: 600, letterSpacing: "0.05em",
        color: isActive ? t.accent : t.text3,
      }}>
        {isActive ? "Active" : "Ended"}
      </Typography>
    </Box>
  );
};

const FteBar = ({ fte, type, t }) => {
  const isBil = type === "BIL";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <Typography sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "8px", color: t.text3,
        letterSpacing: "0.1em", textTransform: "uppercase",
      }}>
        FTE
      </Typography>
      <Box sx={{
        width: "36px", height: "3px",
        background: t.surface3, borderRadius: "2px", overflow: "hidden",
      }}>
        <Box sx={{
          width: `${fte * 100}%`, height: "100%", borderRadius: "2px",
          background: isBil ? t.accent : t.gold,
        }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "14px", fontWeight: 700, color: t.text, lineHeight: 1,
      }}>
        {fte}
      </Typography>
    </Box>
  );
};

const TimelineCard = ({ item, t }) => {
  const isActive = item.status === "ACTIVE";

  return (
    <Box sx={{
      background: isActive ? t.surface : t.surface2,
      border: `1px solid ${isActive ? `${t.accent}44` : t.border}`,
      borderRadius: "8px",
      padding: "14px 16px",
      boxShadow: isActive ? `0 0 0 1px ${t.accent}12, 0 2px 16px ${t.accent}10` : "none",
      transition: "border-color 0.15s, box-shadow 0.15s",
      "&:hover": {
        borderColor: t.border2,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      },
    }}>
      {/* Account name — clean, no competing badges */}
      <Typography sx={{
        fontSize: "15px", fontWeight: 700, color: t.text,
        letterSpacing: "-0.01em", lineHeight: 1.2, mb: "6px",
      }}>
        {item.accountName || "—"}
      </Typography>

      {/* SOW name */}
      {item.sowName && (
        <Typography sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "11px", color: t.text2,
          letterSpacing: "0.02em", mb: "3px",
        }}>
          {item.sowName}
        </Typography>
      )}

      {/* SOW code + Position code as inline badges */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mb: "12px", flexWrap: "wrap" }}>
        {item.sowCode && (
          <Box sx={{
            padding: "1px 6px", borderRadius: "2px",
            background: t.surface3, border: `1px solid ${t.border2}`,
          }}>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3 }}>
              {item.sowCode}
            </Typography>
          </Box>
        )}
        {item.positionCode && (
          <Box sx={{
            padding: "1px 6px", borderRadius: "2px",
            background: t.surface3, border: `1px solid ${t.border2}`,
          }}>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3 }}>
              {item.positionCode}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Bottom row: dates + duration + FTE + type + status */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "12px", color: t.text2, whiteSpace: "nowrap" }}>
            {fmtDate(item.startDate)}
          </Typography>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: t.text3 }}>→</Typography>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "12px", color: t.text2, whiteSpace: "nowrap" }}>
            {fmtDate(item.endDate)}
          </Typography>
        </Box>
        <Box sx={{ padding: "1px 6px", background: t.surface3, borderRadius: "2px" }}>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text3 }}>
            {item.durationMonths}m
          </Typography>
        </Box>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "6px" }}>
          <TypePill type={item.positionType} t={t} />
          <StatusChip status={item.status} t={t} />
          <FteBar fte={item.fte} type={item.positionType} t={t} />
        </Box>
      </Box>
    </Box>
  );
};

const BenchMarker = ({ item, t }) => (
  <Box sx={{ position: "relative", display: "flex", alignItems: "center", pl: ITEM_INDENT, mb: "6px" }}>
    <Box sx={{
      position: "absolute", left: DOT_LEFT,
      width: "7px", height: "7px", borderRadius: "50%",
      background: t.gold, border: `1.5px solid ${t.surface}`,
      opacity: 0.75,
    }} />
    <Box sx={{
      padding: "4px 10px",
      border: `1px dashed ${t.border2}`,
      borderRadius: "3px",
      background: "rgba(214,223,39,0.07)",
    }}>
      <Typography sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "10px", color: t.gold, letterSpacing: "0.04em",
      }}>
        ⌛ &nbsp;SCG Pool &nbsp;·&nbsp; {fmtDate(item.startDate)} – {item.endDate ? fmtDate(item.endDate) : "Present"} &nbsp;·&nbsp; {item.durationMonths}m &nbsp;·&nbsp; {item.positionCode}
      </Typography>
    </Box>
  </Box>
);

const SkeletonCard = ({ t }) => (
  <Box sx={{
    background: t.surface2, border: `1px solid ${t.border}`,
    borderRadius: "8px", padding: "14px 16px", mb: "6px",
  }}>
    {[160, 220, 180].map((w, i) => (
      <Box key={i} sx={{
        height: i === 0 ? "14px" : "11px",
        width: `${w}px`, maxWidth: "100%",
        background: t.surface3, borderRadius: "3px",
        mb: i < 2 ? "8px" : 0, opacity: 0.6,
      }} />
    ))}
  </Box>
);

const EmptyState = ({ t }) => (
  <Box sx={{ display: "flex", justifyContent: "center", padding: "60px 24px" }}>
    <Typography sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "11px", color: t.text3, letterSpacing: "0.06em",
    }}>
      No allocation history found
    </Typography>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────

const SPINE_LEFT  = "20px"; // absolute left from scroll container edge
const ITEM_INDENT = "40px"; // card left offset — clears the spine + dot
const DOT_LEFT    = "14px"; // SPINE_LEFT - half dot width (6px)

const AllocationHistoryDrawer = ({ associate, onClose }) => {
  const theme = useTheme();
  const t     = theme.custom;

  const [history,        setHistory]        = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError,   setHistoryError]   = useState(null);

  const open = Boolean(associate);

  const loadHistory = useCallback(async (id) => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await fetchAllocationHistory(id);
      setHistory(data);
    } catch {
      setHistoryError("Failed to load allocation history");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (associate?.associateId) {
      loadHistory(associate.associateId);
    } else {
      setHistory([]);
    }
  }, [associate?.associateId, loadHistory]);

  const color    = avatarColor(associate?.fullName ?? "");
  const initials = associate?.fullName?.slice(0, 2).toUpperCase() ?? "";
  const yearGroups = groupByYear(history);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 480,
          background: t.surface,
          borderLeft: `1px solid ${t.border}`,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-12px 0 40px rgba(0,0,0,0.10)",
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        padding: "20px 22px 18px",
        borderBottom: `1px solid ${t.border}`,
        display: "flex", alignItems: "flex-start", gap: "13px",
        flexShrink: 0,
      }}>
        <Box sx={{
          width: "40px", height: "40px", borderRadius: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "'DM Mono', monospace", fontSize: "12px", fontWeight: 700,
          background: `${color}22`, border: `1.5px solid ${color}55`, color,
        }}>
          {initials}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "8px", letterSpacing: "0.16em",
            textTransform: "uppercase", color: t.text3, mb: "4px",
          }}>
            Allocation History
          </Typography>
          <Typography sx={{
            fontSize: "16px", fontWeight: 700, color: t.text,
            letterSpacing: "-0.02em", mb: "6px",
          }}>
            {associate?.fullName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "7px", flexWrap: "wrap" }}>
            {associate?.levelCode && (
              <Box sx={{
                background: t.surface3, borderRadius: "2px", padding: "2px 7px",
              }}>
                <Typography sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "10px", fontWeight: 700, color: t.gold,
                }}>
                  {associate.levelCode}
                </Typography>
              </Box>
            )}
            {associate?.levelName && (
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9.5px", color: t.text3 }}>
                · {associate.levelName}
              </Typography>
            )}
            {associate?.joinDate && (
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9.5px", color: t.text3 }}>
                · Joined {fmtDate(associate.joinDate)}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          onClick={onClose}
          sx={{
            width: "28px", height: "28px", borderRadius: "3px",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            color: t.text3, flexShrink: 0, fontSize: "14px",
            transition: "all 0.15s",
            "&:hover": { background: t.surface2, color: t.text },
          }}
        >
          ✕
        </Box>
      </Box>

      {/* ── Timeline ── */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "24px 22px 32px", position: "relative" }}>

        {/* Spine */}
        {!historyLoading && history.length > 0 && (
          <Box sx={{
            position: "absolute",
            left: SPINE_LEFT,
            top: "24px", bottom: "32px",
            width: "1.5px",
            background: `linear-gradient(to bottom, ${t.accent} 0%, ${t.border2} 30%, ${t.border2} 100%)`,
            opacity: 0.45,
            pointerEvents: "none",
          }} />
        )}

        {/* Loading */}
        {historyLoading && (
          <Box>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: "flex", gap: "16px", mb: "6px" }}>
                <Box sx={{ width: "11px", height: "11px", borderRadius: "50%", background: t.surface3, mt: "18px", flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}><SkeletonCard t={t} /></Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Error */}
        {historyError && !historyLoading && (
          <Typography sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "11px", color: "#c84040", padding: "20px 0",
          }}>
            {historyError}
          </Typography>
        )}

        {/* Empty */}
        {!historyLoading && !historyError && history.length === 0 && <EmptyState t={t} />}

        {/* Year groups */}
        {!historyLoading && !historyError && yearGroups.map(([year, items]) => {
          const isCurrentYear = Number(year) === new Date().getFullYear();
          return (
            <Box key={year}>
              {/* Year label */}
              <Box sx={{ pl: ITEM_INDENT, mb: "10px", mt: "4px" }}>
                <Typography sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "9px", fontWeight: 700,
                  letterSpacing: "0.12em", color: t.text3,
                  textTransform: "uppercase",
                }}>
                  {year}{isCurrentYear ? " — Present" : ""}
                </Typography>
              </Box>

              {items.map((item) => {
                // Time in the SCG pool between allocations
                if (item.isBench) {
                  return <BenchMarker key={item.requestId} item={item} t={t} />;
                }

                const isActive = item.status === "ACTIVE";
                return (
                  <Box key={item.requestId} sx={{ position: "relative", pl: ITEM_INDENT, mb: "6px" }}>
                    {/* Dot */}
                    <Box sx={{
                      position: "absolute",
                      left: DOT_LEFT,
                      top: "17px",
                      width: isActive ? "13px" : "11px",
                      height: isActive ? "13px" : "11px",
                      borderRadius: "50%",
                      background: item.positionType === "BIL" ? t.accent : t.gold,
                      border: `2px solid ${t.surface}`,
                      zIndex: 1,
                      ...(isActive && {
                        animation: "psi-pulse 2s ease-in-out infinite",
                      }),
                    }} />
                    <TimelineCard item={item} t={t} />
                  </Box>
                );
              })}

            </Box>
          );
        })}
      </Box>

      <style>{`
        @keyframes psi-pulse {
          0%,100% { box-shadow: 0 0 0 2px rgba(40,179,180,0.3); }
          50%      { box-shadow: 0 0 0 5px rgba(40,179,180,0.1); }
        }
      `}</style>
    </Drawer>
  );
};

export default AllocationHistoryDrawer;
